import { useState } from 'react'
import { useAccount } from '@corratech/pylot-auth-manager'
import { useUser } from '@corratech/pylot-auth-manager'
import { useCountries } from '@corratech/pylot-utils'
import { graphqlFetch, GraphqlFetchOutput } from '@pylot-data/graphqlFetch'
import { CREATE_CUSTOMER_ADDRESS } from '../graphql/createCustomerAddress'
import { CustomerAddress, CustomerAddressInput } from '@pylot-data/fwrdschema'
import { CountryCodeEnum } from '@pylot-data/enums/CountryCodeEnum'
import { getFormatAddressInput } from '../utils/getFormatAddressInput'
import { useRouter } from 'next/router'

export interface AddressInputVariableInterface {
    firstname: string
    lastname: string
    company: string
    telephone: string
    'street[0]': string
    'street[1]': string
    'street[2]': string
    street: string[]
    city: string
    region: string
    region_id: number
    comuna: string
    comuna_id: string
    prefix_phone: string
    postcode: string
    country_code: CountryCodeEnum
    default_billing: boolean
    default_shipping: boolean
    save_in_address_book?: boolean
    document_number: string
}

interface AddressVariableInterface {
    address: CustomerAddressInput
}

export interface AddressOutputInterface {
    createCustomerAddress: CustomerAddress
}

export interface UseAddressInterface {
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
    isLoading: boolean
    createCustomerAddress: (
        formInput: AddressInputVariableInterface
    ) => Promise<GraphqlFetchOutput<AddressOutputInterface>>
}

export const useCreateAddress = (): UseAddressInterface => {
    const { locale } = useRouter()
    const [isLoading, setIsLoading] = useState(false)
    const { addresses } = useUser({
        revalidateOnFocus: false
    })
    const { mutate } = useAccount({
        revalidateOnFocus: false
    })
    const { getCountryByCode } = useCountries({
        revalidateOnFocus: false
    })

    const createCustomerAddress = async (
        formInput: AddressInputVariableInterface
    ) => {
        const countryByCode = getCountryByCode(formInput?.country_code)
        setIsLoading(true)
        const isDefault = !!(
            formInput.default_shipping || formInput.default_billing
        )

        const finalInput = {
            ...formInput,
            default_shipping: !!formInput.default_shipping,
            default_billing: isDefault
        }

        if (!addresses || addresses.length === 0) {
            finalInput.default_billing = true
            finalInput.default_shipping = true
        }

        const response = await graphqlFetch<
            AddressVariableInterface,
            AddressOutputInterface
        >({
            query: CREATE_CUSTOMER_ADDRESS,
            variables: {
                address: getFormatAddressInput(finalInput, countryByCode)
            },
            locale
        })

        await mutate()
        setIsLoading(false)
        return response
    }

    return {
        isLoading,
        setIsLoading,
        createCustomerAddress
    }
}
